import React, { createContext, useContext, useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import AppButton from '@/components/atoms/AppButton';
import { MdClose } from 'react-icons/md';
import {
  XMarkIcon,
  CheckIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { Caption, Heading, Label } from '@/components';
import { APP_BG, APP_BORDER, APP_TEXT } from '@/constant/theme/colors.constant';
import { tid } from '@/utils/testId';
import { ELEMENT_TEST_IDS } from '@/constant/testIds/elements.testids';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ModalVariant =
  | 'default'
  | 'danger'
  | 'warning'
  | 'info'
  | 'success';
export type ModalSize =
  | 'xs'
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl'
  | '4xl'
  | '5xl';

// ─── Context ──────────────────────────────────────────────────────────────────

interface ModalContextValue {
  variant: ModalVariant;
  onClose: () => void;
  titleId: string;
  subtitleId: string;
}

const ModalContext = createContext<ModalContextValue>({
  variant: 'default',
  onClose: () => { },
  titleId: 'modal-title',
  subtitleId: 'modal-subtitle',
});
const { MODAL_TID } = ELEMENT_TEST_IDS;

const useModal = () => useContext(ModalContext);

// ─── Style maps ───────────────────────────────────────────────────────────────

const stripeGradient: Record<ModalVariant, string> = {
  default: 'from-primary via-secondary to-tertiary',
  danger: 'from-red-500 to-red-600',
  warning: 'from-yellow-400 to-amber-500',
  info: 'from-blue-500 to-blue-600',
  success: 'from-emerald-500 to-emerald-600',
};

const iconBgGradient: Record<ModalVariant, string> = {
  default: 'from-primary to-secondary',
  danger: 'from-red-500 to-red-600',
  warning: 'from-yellow-400 to-amber-500',
  info: 'from-blue-500 to-blue-600',
  success: 'from-emerald-500 to-emerald-600',
};

const confirmBtnClasses: Record<ModalVariant, string> = {
  default: 'from-primary to-secondary hover:opacity-90',
  danger: 'from-red-500 to-red-600 hover:opacity-90',
  warning: 'from-yellow-400 to-amber-500 hover:opacity-90',
  info: 'from-blue-500 to-blue-600 hover:opacity-90',
  success: 'from-emerald-500 to-emerald-600 hover:opacity-90',
};

const headerBgClasses: Record<ModalVariant, string> = {
  default: 'bg-gradient-to-r from-primary/10 via-secondary/10 to-tertiary/10',
  danger: 'bg-gradient-to-r from-red-500/10 to-red-600/10',
  warning: 'bg-gradient-to-r from-yellow-400/10 to-amber-500/10',
  info: 'bg-gradient-to-r from-blue-500/10 to-blue-600/10',
  success: 'bg-gradient-to-r from-emerald-500/10 to-emerald-600/10',
};

const inputFocusClasses: Record<ModalVariant, string> = {
  default:
    '[&_input:focus]:ring-primary [&_input:focus]:border-primary [&_select:focus]:ring-primary [&_select:focus]:border-primary [&_textarea:focus]:ring-primary [&_textarea:focus]:border-primary',
  danger:
    '[&_input:focus]:ring-red-500 [&_input:focus]:border-red-500 [&_select:focus]:ring-red-500 [&_select:focus]:border-red-500 [&_textarea:focus]:ring-red-500 [&_textarea:focus]:border-red-500',
  warning:
    '[&_input:focus]:ring-yellow-400 [&_input:focus]:border-yellow-400 [&_select:focus]:ring-yellow-400 [&_select:focus]:border-yellow-400 [&_textarea:focus]:ring-yellow-400 [&_textarea:focus]:border-yellow-400',
  info: '[&_input:focus]:ring-blue-500 [&_input:focus]:border-blue-500 [&_select:focus]:ring-blue-500 [&_select:focus]:border-blue-500 [&_textarea:focus]:ring-blue-500 [&_textarea:focus]:border-blue-500',
  success:
    '[&_input:focus]:ring-emerald-500 [&_input:focus]:border-emerald-500 [&_select:focus]:ring-emerald-500 [&_select:focus]:border-emerald-500 [&_textarea:focus]:ring-emerald-500 [&_textarea:focus]:border-emerald-500',
};

const closeBtnVariant: Record<
  ModalVariant,
  'secondary' | 'danger' | 'warning' | 'info' | 'success'
> = {
  default: 'secondary',
  danger: 'danger',
  warning: 'warning',
  info: 'info',
  success: 'success',
};

const sizeClasses: Record<ModalSize, string> = {
  xs: 'max-w-xs',
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'w-[90vw] max-w-[90vw]',
};

// ─── Root ─────────────────────────────────────────────────────────────────────

interface ModalRootProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  variant?: ModalVariant;
  size?: ModalSize;
}

const ModalRoot = ({
  open,
  onClose,
  children,
  variant = 'default',
  size = 'md',
}: ModalRootProps) => {
  const uid = useId();
  const titleId = `${uid}-title`;
  const subtitleId = `${uid}-subtitle`;

  // Escape key
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Lock scroll + compensate scrollbar width to prevent layout shift
  useEffect(() => {
    if (!open) return;
    const sw = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${sw}px`;
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <ModalContext.Provider value={{ variant, onClose, titleId, subtitleId }}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={subtitleId}
        className={classNames('fixed inset-0 z-[1100]', inputFocusClasses[variant])}
      >
        {/* Backdrop — click-outside closes */}
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
          onClick={onClose}
          aria-hidden="true"
        />

        {/* Scroll layer: overflow-y-auto alone — no flex centering here.
             Flexbox centering + overflow-y-auto on the same element causes the top
             of tall content to be pushed above the scroll boundary (unreachable). */}
        <div className="absolute inset-0 overflow-y-auto pointer-events-none">
          {/* Centering wrapper: min-h-full makes it at least 100vh so items-center
               works for short modals; tall modals just grow past 100% and scroll. */}
          <div className="flex min-h-full items-center justify-center px-4 pt-20 pb-4">
            <div
              className={classNames(
                'relative w-full pointer-events-auto overflow-hidden',
                'flex flex-col max-h-[calc(100vh-6rem)]',
                `rounded-2xl ${APP_BG.base}`,
                'shadow-[0_24px_60px_-10px_rgba(0,0,0,0.22)] dark:shadow-[0_24px_60px_-10px_rgba(0,0,0,0.6)]',
                'ring-1 ring-black/[0.05] dark:ring-white/[0.07]',
                sizeClasses[size],
              )}
            >
              {children}
            </div>
          </div>
        </div>
      </div>
    </ModalContext.Provider>,
    document.body,
  );
};

// ─── Header ───────────────────────────────────────────────────────────────────

interface ModalHeaderProps {
  /** Modal title (required for accessibility) */
  title: string;
  /** Optional subtitle rendered below the title */
  subtitle?: string;
  /** Optional leading icon rendered inside a gradient circle */
  icon?: React.ReactNode;
  /** Override the close handler; defaults to the root onClose */
  onClose?: () => void;
}

const ModalHeader = ({ title, subtitle, icon, onClose }: ModalHeaderProps) => {
  const { variant, onClose: ctxClose, titleId, subtitleId } = useModal();
  const handleClose = onClose ?? ctxClose;

  return (
    <>
      <div
        className={classNames(
          'flex items-center justify-between gap-4 px-6 py-4 shrink-0',
          headerBgClasses[variant],
        )}
      >
        <div className="flex items-center gap-3 min-w-0">
          {icon && (
            <div
              className={classNames(
                'w-9 h-9 rounded-xl flex items-center justify-center shrink-0 shadow-sm text-white bg-gradient-to-br',
                iconBgGradient[variant],
              )}
            >
              {icon}
            </div>
          )}
          <div className="min-w-0">
            <Heading size="lg" color="default" leading="none">
              {title}
            </Heading>
            {subtitle && (
              <Caption weight="medium" size="md" leading="none" color="subtle">
                {subtitle}
              </Caption>
            )}
          </div>
        </div>

        {/* Close × button */}
        <AppButton
          variant={closeBtnVariant[variant]}
          tint={variant !== 'default'}
          shape="square"
          size="md"
          onClick={handleClose}
          aria-label="Close"
          className="shrink-0 transition-all duration-200 hover:rotate-90"
          icon={<MdClose className="w-4 h-4" />}
          dataTestId={tid(`${title}-${MODAL_TID.CLOSE_BTN}`)}
        />
      </div>
      <div
        className={classNames(
          'h-[3px] w-full shrink-0 bg-gradient-to-r',
          stripeGradient[variant],
        )}
      />
    </>
  );
};

// ─── Body ─────────────────────────────────────────────────────────────────────

interface ModalBodyProps {
  children: React.ReactNode;
  /** Extra Tailwind classes applied to the wrapper div */
  className?: string;
  /** Removes default padding when true (useful for full-bleed content) */
  noPadding?: boolean;
}

const ModalBody = ({ children, className, noPadding }: ModalBodyProps) => (
  <div
    className={classNames(
      'flex-1 min-h-0 overflow-y-auto',
      !noPadding && 'px-6 py-4',
      APP_TEXT.soft,
      className,
    )}
  >
    {children}
  </div>
);

// ─── Footer ───────────────────────────────────────────────────────────────────

interface ModalFooterProps {
  children: React.ReactNode;
  className?: string;
  /** Aligns footer buttons to the left instead of the right */
  align?: 'left' | 'right' | 'center';
}

const alignClasses: Record<NonNullable<ModalFooterProps['align']>, string> = {
  left: 'justify-start',
  right: 'justify-end',
  center: 'justify-center',
};

const ModalFooter = ({
  children,
  className,
  align = 'right',
}: ModalFooterProps) => (
  <div
    className={classNames(
      'flex flex-wrap items-center gap-3 px-6 py-4 shrink-0 [&>*]:whitespace-nowrap [&>*]:shrink-0',
      APP_BG.overlay,
      `border-t ${APP_BORDER.soft}`,
      alignClasses[align],
      className,
    )}
  >
    {children}
  </div>
);

// ─── CancelButton ─────────────────────────────────────────────────────────────

interface ModalButtonProps {
  onClick?: () => void;
  children: React.ReactNode;
  disabled?: boolean;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  loading?: boolean;
  icon?: React.ReactNode;
  testId?: string;
}

const CancelButton = ({
  onClick,
  children,
  disabled,
  className,
  type = 'button',
  icon,
  testId
}: ModalButtonProps) => (
  <AppButton
    type={type}
    variant="neutral"
    size="sm"
    icon={icon ?? <XMarkIcon />}
    onClick={onClick}
    disabled={disabled}
    className={className}
    dataTestId={testId ? tid(testId) : tid(MODAL_TID.CANCEL_BTN)}
  >
    {children}
  </AppButton>
);

// ─── ResetButton ──────────────────────────────────────────────────────────────

const ResetButton = ({
  onClick,
  children,
  disabled,
  className,
  type = 'button',
  icon,
  testId
}: ModalButtonProps) => (
  <AppButton
    type={type}
    variant="primary"
    tint
    size="sm"
    icon={icon ?? <ArrowPathIcon />}
    onClick={onClick}
    disabled={disabled}
    className={className}
    dataTestId={testId ? tid(testId) : tid(MODAL_TID.RESET_BTN)}
  >
    {children}
  </AppButton>
);

// ─── ConfirmButton ────────────────────────────────────────────────────────────

const variantButtonMap: Record<
  ModalVariant,
  'primary' | 'danger' | 'warning' | 'success'
> = {
  default: 'primary',
  danger: 'danger',
  warning: 'warning',
  info: 'primary',
  success: 'success',
};

const ConfirmButton = ({
  onClick,
  children,
  disabled,
  className,
  type = 'button',
  loading,
  icon,
  testId
}: ModalButtonProps) => {
  const { variant } = useModal();

  return (
    <AppButton
      type={type}
      variant={variantButtonMap[variant]}
      size="sm"
      icon={icon}
      loading={loading}
      onClick={onClick}
      disabled={disabled}
      className={className}
      dataTestId={testId ? testId : tid(`${type}-${MODAL_TID.CONFIRM_BTN}`)}
    >
      {children}
    </AppButton>
  );
};

// ─── Divider (optional utility) ───────────────────────────────────────────────

const ModalDivider = ({ className }: { className?: string }) => (
  <hr className={classNames(`border-t ${APP_BORDER.soft}`, className)} />
);

export const Modal = Object.assign(ModalRoot, {
  Header: ModalHeader,
  Body: ModalBody,
  Footer: ModalFooter,
  CancelButton,
  ResetButton,
  ConfirmButton,
  Divider: ModalDivider,
});
